import { useMemo } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import { Box, ButtonBase, Chip, Stack, Typography, alpha } from '@mui/material'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import { ApprovalStatePolicy } from '@/types/approvals'
import { AuthUser } from '@/types/users'
import { ApprovalColumn, ApprovalTableRow } from '@/utils/approval-columns'
import { approvalProgress } from '@/utils/my-approvals'
import { formatValue } from '@/components/purchase-order-view/shared'
import ApprovalReviewers from './ApprovalReviewers'

type UseApprovalColumnsOptions = {
  columns: ApprovalColumn[]
  usersById: Map<string, AuthUser>
  usersPending: boolean
  onOpenPolicy: (policy: ApprovalStatePolicy) => void
}

type PolicyButtonProps = {
  description: string
  onOpen: () => void
}

function PolicyButton({ description, onOpen }: PolicyButtonProps) {
  return (
    <ButtonBase
      title={description}
      aria-label={`Policy details for ${description}`}
      // The whole row opens the document, so this click stops here.
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
      onKeyDown={(event) => event.stopPropagation()}
      sx={{
        maxWidth: '100%',
        justifyContent: 'flex-start',
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        '&:hover, &:focus-visible': { bgcolor: 'action.selected' },
      }}
    >
      <Typography variant="body2" noWrap>
        {description}
      </Typography>
    </ButtonBase>
  )
}

type ProgressCellProps = { policy: ApprovalStatePolicy | null }

function ProgressCell({ policy }: ProgressCellProps) {
  if (!policy) {
    return (
      <Typography variant="caption" color="text.disabled">
        Policy removed
      </Typography>
    )
  }

  const { approved, needed, rejected } = approvalProgress(policy)
  return (
    <Stack>
      <Typography variant="caption" fontWeight={600}>
        {approved}
        <Box component="span" sx={{ color: 'text.disabled' }}>
          /{needed}
        </Box>
      </Typography>
      {rejected > 0 && (
        <Typography variant="caption" color="error.main" fontWeight={600}>
          {rejected} rejection{rejected === 1 ? '' : 's'}
        </Typography>
      )}
    </Stack>
  )
}

export default function useApprovalColumns({
  columns,
  usersById,
  usersPending,
  onOpenPolicy,
}: UseApprovalColumnsOptions) {
  const getThemeColor = useGetThemeColor()

  return useMemo<MRT_ColumnDef<ApprovalTableRow>[]>(() => {
    const cells: Record<string, MRT_ColumnDef<ApprovalTableRow>['Cell']> = {
      name: ({ row }) => {
        const { detail } = row.original
        return (
          <Typography
            variant="body2"
            fontWeight={500}
            title={detail?.vendorName || detail?.name || undefined}
            sx={{
              minWidth: 0,
              width: '100%',
              overflowWrap: 'anywhere',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
            }}
          >
            {detail?.vendorName || detail?.name || '—'}
          </Typography>
        )
      },
      policy: ({ row }) => {
        const { policy } = row.original
        return policy?.description ? (
          <PolicyButton
            description={policy.description}
            onOpen={() => onOpenPolicy(policy)}
          />
        ) : (
          <Typography variant="body2" noWrap>
            —
          </Typography>
        )
      },
      state: ({ row }) => {
        const workflowState = row.original.detail?.workflowState
        if (!workflowState) {
          return (
            <Typography variant="body2" color="text.disabled">
              No Workflow State
            </Typography>
          )
        }
        const stateColor = getThemeColor(workflowState.color)
        return (
          <Chip
            size="small"
            label={workflowState.name}
            sx={{
              maxWidth: '100%',
              fontWeight: 600,
              color: stateColor,
              bgcolor: alpha(stateColor, 0.16),
            }}
          />
        )
      },
      decision: ({ row }) => {
        if (!row.original.action) return null
        const approved = row.original.action.type === 'approval'
        return (
          <Chip
            size="small"
            label={approved ? 'Approved' : 'Rejected'}
            color={approved ? 'success' : 'error'}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        )
      },
      time: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.original.time}
        </Typography>
      ),
      amount: ({ row }) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {formatValue(row.original.detail?.amount ?? null, {
            decimals: 2,
            prefix: '$',
          })}
        </Typography>
      ),
      progress: ({ row }) => <ProgressCell policy={row.original.policy} />,
      reviewers: ({ row }) =>
        row.original.policy ? (
          <ApprovalReviewers
            policy={row.original.policy}
            usersById={usersById}
            usersPending={usersPending}
            action={row.original.action}
          />
        ) : null,
    }

    return columns.map(({ key, label, size, grow, align }) => ({
      id: key,
      header: label,
      size,
      grow,
      Cell: cells[key],
      muiTableHeadCellProps: { align },
      muiTableBodyCellProps: { align },
    }))
  }, [columns, getThemeColor, usersById, usersPending, onOpenPolicy])
}
